// ============================================================
// MySQLCrudOperations.jsx
//
// CRUD Operations (SELECT, INSERT, UPDATE, DELETE)
// Foundation · Module 03
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

/* Icons */
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CodeIcon from "@mui/icons-material/Code";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SecurityIcon from "@mui/icons-material/Security";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "rgba(0,0,0,0.45)",
    codeText: "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
};

/* ───────────────── SUB COMPONENTS ───────────────── */

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 18px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                <Typography sx={{ fontWeight: 700, color: BRAND.white }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.7 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map(l => l.cmd).join("\n");

    const copy = () => {
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map(c => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <Box onClick={copy} sx={{ cursor: "pointer", color: BRAND.gold }}>
                        {copied ? <DoneIcon /> : <ContentCopyIcon />}
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ p: 2 }}>
                {lines.map((l, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                        {l.comment && (
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.muted }}>
                                -- {l.comment}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText }}>
                            {l.cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 2 }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    background: BRAND.surface,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                    <Typography sx={{ color: BRAND.white }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>

            <Collapse in={open}>
                <Box sx={{ p: 2, background: "rgba(0,0,0,0.25)" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

/* ───────────────── MAIN PAGE ───────────────── */

export default function MySQLCrudOperations() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Foundation · Module 03"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ ...sectionHeading, mb: 2 }}>
                    CRUD Operations <span style={{ color: BRAND.gold }}>(SELECT · INSERT · UPDATE · DELETE)</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 720 }}>
                    CRUD operations are the daily language of every backend developer.
                    Mastering SELECT, INSERT, UPDATE, and DELETE means you can read, write,
                    modify, and remove data safely and confidently.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* WHY CRUD */}
                <SectionTitle icon={<LightbulbIcon />} label="What Is CRUD?" />
                <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                    CRUD stands for <strong>Create</strong>, <strong>Read</strong>, <strong>Update</strong>, and <strong>Delete</strong>.
                    Every application—banking, e‑commerce, learning platforms—relies on these four operations.
                    SQL gives you precise control over how they happen.
                </Typography>

                {/* KEY CONCEPTS */}
                <SectionTitle icon={<MenuBookIcon />} label="CRUD Concepts" />
                <Grid container spacing={2}>
                    {[
                        { icon: <TableChartIcon />, title: "SELECT", description: "Read data from tables using filters, sorting, and limits." },
                        { icon: <AddCircleOutlineIcon />, title: "INSERT", description: "Add new rows safely using column lists and constraints." },
                        { icon: <EditIcon />, title: "UPDATE", description: "Modify existing data—always with a WHERE clause." },
                        { icon: <DeleteOutlineIcon />, title: "DELETE", description: "Remove data carefully; mistakes here are permanent." },
                        { icon: <SecurityIcon />, title: "WHERE Clause", description: "Controls exactly which rows are affected. Missing WHERE is the #1 beginner mistake." },
                    ].map(c => (
                        <Grid item xs={12} sm={6} md={4} key={c.title}>
                            <ConceptCard {...c} />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* PRACTICE */}
                <SectionTitle icon={<CodeIcon />} label="Try It Yourself (CRUD Lab)" />

                <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>
                    🟡 SELECT – Read Data
                </Typography>
                <CodeBlock
                    lines={[
                        { comment: "Read all rows", cmd: "SELECT * FROM students;" },
                        { comment: "Select specific columns", cmd: "SELECT full_name, email FROM students;" },
                        { comment: "Filter results", cmd: "SELECT * FROM students WHERE id = 2;" },
                        { comment: "Sort and limit", cmd: "SELECT * FROM students ORDER BY created_at DESC LIMIT 2;" },
                    ]}
                />

                <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>
                    🟡 INSERT – Create Data
                </Typography>
                <CodeBlock
                    lines={[
                        {
                            comment: "Insert a new row",
                            cmd: "INSERT INTO students (full_name, email) VALUES ('Linus Torvalds', 'linus@academy.com');",
                        },
                        {
                            comment: "Insert multiple rows",
                            cmd: `INSERT INTO students (full_name, email) VALUES
('Margaret Hamilton', 'margaret@academy.com'),
('Tim Berners-Lee', 'tim@academy.com');`,
                        },
                    ]}
                />

                <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>
                    🟡 UPDATE – Modify Data
                </Typography>
                <CodeBlock
                    lines={[
                        {
                            comment: "Update one row safely",
                            cmd: "UPDATE students SET email = 'alan.turing@academy.com' WHERE id = 2;",
                        },
                        {
                            comment: "Check affected rows first",
                            cmd: "SELECT * FROM students WHERE id = 2;",
                        },
                    ]}
                />

                <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>
                    🟡 DELETE – Remove Data
                </Typography>
                <CodeBlock
                    lines={[
                        {
                            comment: "Delete a specific row",
                            cmd: "DELETE FROM students WHERE id = 5;",
                        },
                        {
                            comment: "Always preview with SELECT",
                            cmd: "SELECT * FROM students WHERE id = 5;",
                        },
                    ]}
                />

                {/* ADVANCED */}
                <Typography sx={{ color: BRAND.white, fontWeight: 700, mt: 4, mb: 2 }}>
                    🔽 Deeper Examples (click to expand)
                </Typography>

                <ExpandableExample title="Safe UPDATE & DELETE patterns" icon={<SecurityIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Preview rows first",
                                cmd: "SELECT * FROM students WHERE email LIKE '%academy.com';",
                            },
                            {
                                comment: "Then update",
                                cmd: "UPDATE students SET active = 0 WHERE email LIKE '%academy.com';",
                            },
                        ]}
                    />
                </ExpandableExample>

                <ExpandableExample title="Transactions for CRUD safety" icon={<SecurityIcon />}>
                    <CodeBlock
                        lines={[
                            { comment: "Start transaction", cmd: "START TRANSACTION;" },
                            { comment: "Make changes", cmd: "DELETE FROM students WHERE id = 99;" },
                            { comment: "Undo if unsure", cmd: "ROLLBACK;" },
                            { comment: "Confirm when sure", cmd: "COMMIT;" },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* LEARNED */}
                <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                <Grid container spacing={2}>
                    {[
                        "How CRUD maps to real application behavior",
                        "How to read data with SELECT",
                        "How to insert new records safely",
                        "How to update existing data correctly",
                        "How to delete data without disasters",
                        "Why WHERE clauses are critical",
                        "How transactions protect your data",
                    ].map(item => (
                        <Grid item xs={12} sm={6} key={item}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold }} />
                                <Typography sx={{ color: BRAND.muted }}>{item}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* FOOTER TIP */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        💡 <strong>Pro Tip:</strong> Before every UPDATE or DELETE, run the same
                        WHERE clause with SELECT. If SELECT returns the wrong rows, so will UPDATE or DELETE.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
